import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import Add from '@mui/icons-material/Add'
import FitScreen from '@mui/icons-material/FitScreen'
import Remove from '@mui/icons-material/Remove'
import {
  areaMotherIndex,
  buildOrgTree,
  dataBr,
  fetchOrgChart,
  fetchOrgChartPeople,
  groupJobs,
  ORG_CHART_QUERY_KEY,
  orgChartPeopleKey,
  layoutAreaMap,
  layoutJobBands,
  mapColumnWidth,
  wrapLines,
  MAPA_PAD,
  planBands,
  setUnitAdvisory,
  splitAdvisory,
  staffedElsewhere,
  type AreaBlock,
  type IndentedJob,
  type OrgChart,
  type PlacedJob,
  type OrgChartJob,
  type OrgChartNode,
  type OrgChartStaffed,
} from '../api/orgChart'
import { suggestFunction } from '../api/bands'
import { fetchMe, ME_QUERY_KEY } from '../api/session'
import { ErrorState } from './ErrorState'
import { LoadingState } from './LoadingState'
import { brand } from '../theme'
import PALETA_AREAS from '../theme/paleta_areas.json'
import { t } from '../i18n'

/**
 * s61 / E2 (P1-P4 da s57) - organograma em CAIXAS, de cima para baixo: cada caixa e uma area com os cargos e o
 * numero de pessoas. Clique no cargo mostra os NOMES -- so para quem a API diz `can_see_names` (RH e
 * administradores). "Folha de dd/mm/aaaa" sempre visivel (P4). Cargo sem area nao some: caixa "Cargos sem area".
 *
 * s62 / fatia s60c (decisoes de Marcelo de 19/10/2026):
 *  O1  janela na altura da tela com as DUAS barras de rolagem sempre visiveis; arrastar o fundo move; zoom -, + e
 *      "ajustar a tela" (escala por transform, com a area de rolagem do tamanho ja escalado).
 *  O2  a caixa conta pela LOTACAO (`people_here` + filhas); o cargo aparece tambem onde ha gente dele
 *      ("Lotados aqui, de outras areas"); na area-casa o numero e o de quem esta la, com "de N no total".
 *  O2b rascunho que muda a area: "rascunho: vai para X" (o desenho segue a publicada).
 *  O3  subordinacao com recuo dentro da caixa; "responde a ..." quando o chefe esta em outra area; visao "Cargos".
 *  C1  cores da marca por nivel (DIRECAO navy solido; gerencias gradiente; areas branco com faixa azul-clara;
 *      subareas tinta azul-clara; linhas navy de 2 px; selo de pessoas com acento laranja; sem area faixa cinza).
 *  G1  cargo com 0 pessoas fica cinza claro, "vago" -- so quando a data da folha esta preenchida.
 */

const LINHA = 2
const ZOOM_MIN = 0.25
const ZOOM_MAX = 2
const ZOOM_PASSO = 0.25

interface Tom {
  bg: string
  fg: string
  fg2: string
  faixa: string | null
  borda: string
  escuro: boolean
}


// cargo sem area: a caixa mostra o TOTAL do cargo (nao ha area-casa para descontar quem esta lotado fora)
const SEM_FORA = new Map<string, number>()
const TOM_SEM_AREA: Tom = { bg: '#FFFFFF', fg: brand.navy, fg2: 'text.secondary', faixa: brand.grey, borda: '#CBD5E1', escuro: false }

function Selo({ n, tom }: { n: number; tom: Tom }) {
  return (
    <Box
      component="span"
      data-testid="selo-pessoas"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.75,
        py: 0.125,
        borderRadius: 10,
        border: `1.5px solid ${brand.orange}`,
        fontSize: '0.75rem',
        lineHeight: 1.4,
        color: tom.fg,
      }}
    >
      {t.units.orgChart.people(n)}
    </Box>
  )
}

function LinhaCargo({
  job,
  nivel,
  chefeFora,
  aqui,
  podeNomes,
  abrir,
  tom,
  comFolha,
}: {
  job: OrgChartJob
  nivel: number
  chefeFora: string | null
  aqui: number
  podeNomes: boolean
  abrir: (j: OrgChartJob) => void
  tom: Tom
  comFolha: boolean
}) {
  const vago = comFolha && job.people === 0
  const conteudo = (
    <Box sx={{ width: '100%', textAlign: 'left' }}>
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="baseline">
        <Typography variant="body2" sx={{ textAlign: 'left', color: vago ? 'text.secondary' : 'inherit' }}>
          {nivel > 0 && (
            <Box component="span" aria-hidden sx={{ color: tom.fg2, mr: 0.5 }}>
              └
            </Box>
          )}
          {job.jobName}
        </Typography>
        <Typography variant="body2" sx={{ color: vago ? 'text.secondary' : tom.fg2, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {vago ? t.units.orgChart.vacant : aqui}
        </Typography>
      </Stack>
      {aqui !== job.people && (
        <Typography variant="caption" component="p" sx={{ color: tom.fg2 }}>
          {t.units.orgChart.ofTotal(job.people)}
        </Typography>
      )}
      {chefeFora && (
        <Typography variant="caption" component="p" sx={{ color: tom.fg2 }}>
          {t.units.orgChart.reportsTo(chefeFora)}
        </Typography>
      )}
      {job.pendingAreaName && (
        <Typography variant="caption" component="p" sx={{ color: tom.escuro ? brand.orangeLight : brand.orangeDark, fontWeight: 600 }}>
          {t.units.orgChart.pendingArea(job.pendingAreaName)}
        </Typography>
      )}
    </Box>
  )
  const sx = {
    width: '100%',
    py: 0.25,
    px: 0.5,
    ml: nivel * 1.5,
    borderRadius: 1,
    ...(vago ? { bgcolor: '#F1F5F9', color: 'text.secondary' } : {}),
  }
  if (!podeNomes) {
    return (
      <Box data-testid={`cargo-linha-${job.jobId}`} data-nivel={nivel} data-vago={vago ? 'sim' : 'nao'} sx={sx}>
        {conteudo}
      </Box>
    )
  }
  return (
    <ButtonBase
      onClick={() => abrir(job)}
      data-testid={`cargo-linha-${job.jobId}`}
      data-nivel={nivel}
      data-vago={vago ? 'sim' : 'nao'}
      aria-label={t.units.orgChart.seeNames(job.jobName, job.people)}
      sx={{ ...sx, width: `calc(100% - ${nivel * 12}px)`, '&:hover, &:focus-visible': { bgcolor: tom.escuro ? 'rgba(255,255,255,0.12)' : 'action.hover' } }}
    >
      {conteudo}
    </ButtonBase>
  )
}

function Caixa({
  titulo,
  total,
  jobs,
  staffed = [],
  fora,
  podeNomes,
  abrir,
  tom,
  comFolha,
}: {
  titulo: string
  total: number
  jobs: OrgChartJob[]
  staffed?: OrgChartStaffed[]
  fora: Map<string, number>
  podeNomes: boolean
  abrir: (j: OrgChartJob) => void
  tom: Tom
  comFolha: boolean
}) {
  const grupos = useMemo(() => groupJobs(jobs), [jobs])
  const varias = grupos.length > 1
  const linha = (l: IndentedJob) => (
    <LinhaCargo
      key={l.job.jobId}
      job={l.job}
      nivel={l.level}
      chefeFora={l.outsideBoss}
      aqui={Math.max(0, l.job.people - (fora.get(l.job.jobId) ?? 0))}
      podeNomes={podeNomes}
      abrir={abrir}
      tom={tom}
      comFolha={comFolha}
    />
  )
  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'inline-block',
        minWidth: 210,
        maxWidth: varias ? 'none' : 280,
        p: 1.25,
        textAlign: 'left',
        verticalAlign: 'top',
        background: tom.bg,
        color: tom.fg,
        borderColor: tom.borda,
        ...(tom.faixa ? { borderTop: `4px solid ${tom.faixa}` } : {}),
      }}
    >
      <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700, lineHeight: 1.3, color: 'inherit' }}>
        {titulo}
      </Typography>
      <Box sx={{ mt: 0.25, mb: 0.75 }}>
        <Selo n={total} tom={tom} />
      </Box>
      {jobs.length === 0 && staffed.length === 0 ? (
        <Typography variant="caption" sx={{ color: tom.fg2 }}>
          {t.units.orgChart.noJobs}
        </Typography>
      ) : (
        varias ? (
          // s62 / C2: uma coluna por chefia, lado a lado; avulsos na coluna final
          <Stack direction="row" spacing={1.5} alignItems="flex-start" data-testid="colunas-chefia">
            {grupos.map((g, i) => (
              <Stack
                key={g.chefe ?? 'avulsos'}
                spacing={0.25}
                data-testid={`grupo-${g.chefe ?? 'avulsos'}`}
                sx={{
                  minWidth: 190,
                  maxWidth: 260,
                  ...(i > 0 ? { pl: 1.5, borderLeft: `1px solid ${tom.escuro ? 'rgba(255,255,255,0.3)' : '#CBD5E1'}` } : {}),
                }}
              >
                {g.linhas.map(linha)}
              </Stack>
            ))}
          </Stack>
        ) : (
          <Stack spacing={0.25}>{grupos.flatMap((g) => g.linhas).map(linha)}</Stack>
        )
      )}
      {staffed.length > 0 && (
        <Box data-testid="lotados-aqui" sx={{ mt: 0.75, pt: 0.5, borderTop: 1, borderColor: tom.escuro ? 'rgba(255,255,255,0.25)' : 'divider' }}>
          <Typography variant="caption" component="p" sx={{ color: tom.fg2, fontWeight: 600 }}>
            {t.units.orgChart.staffedHere}
          </Typography>
          {staffed.map((s) => (
            <Stack key={s.jobId} direction="row" spacing={1} justifyContent="space-between" sx={{ px: 0.5 }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                {s.jobName}
              </Typography>
              <Typography variant="body2" sx={{ color: tom.fg2, fontVariantNumeric: 'tabular-nums' }}>
                {s.people}
              </Typography>
            </Stack>
          ))}
        </Box>
      )}
    </Paper>
  )
}

interface Comum {
  podeNomes: boolean
  abrir: (j: OrgChartJob) => void
  fora: Map<string, number>
  comFolha: boolean
}

const MAPA_GAP = 4
const MAPA_BLOCO_H = 64

/**
 * s62 / MAPA DE AREAS (modelo XTURBO): blocos encaixados, cartao BRANCO e a cor so na faixa do topo (area-mae, marca
 * v1.6.0; raiz = navy). Nome sempre na HORIZONTAL (Marcelo 20/10 14:14). Clique na area abre os cargos dela.
 */
function MapaAreas({ arvore, podeMarcar, ...c }: Comum & { arvore: OrgChartNode[]; podeMarcar: boolean }) {
  const [aberta, setAberta] = useState<OrgChartNode | null>(null)
  // s62 / C3b-2 (X3): areas de ASSESSORIA saem da arvore e vao para a coluna lateral (a cor segue a da arvore inteira)
  const { main, advisory } = useMemo(() => splitAdvisory(arvore), [arvore])
  const lay = useMemo(() => layoutAreaMap(main), [main])
  const porId = new Map<string, OrgChartNode>()
  const junta = (n: OrgChartNode) => {
    porId.set(n.unit.id, n)
    n.children.forEach(junta)
  }
  arvore.forEach(junta)
  const cores = areaMotherIndex([...porId.values()].map((n) => n.unit))
  // s62 (Marcelo 20/10 16:12 "algumas estao cortando no meio"): a coluna tem no minimo a largura da MAIOR PALAVRA dos
  // nomes -- o nome quebra so entre palavras; a altura do nivel cresce com as linhas que o nome realmente ocupa
  const MAPA_COL = useMemo(() => mapColumnWidth([...porId.values()].map((n) => n.unit.name)), [arvore]) // eslint-disable-line react-hooks/exhaustive-deps
  const alt: number[] = []
  const linhas = (b: AreaBlock) => wrapLines(porId.get(b.id)?.unit.name ?? '', b.cols * MAPA_COL - MAPA_GAP - MAPA_PAD)
  const hb: number[] = []
  for (const b of lay.blocks) hb[b.depth] = Math.max(hb[b.depth] ?? 0, MAPA_BLOCO_H + Math.max(0, linhas(b) - 2) * 14)
  // nivel com pilha de folhas: cresce o bastante para a pilha mais alta do nivel
  for (let d = 0; d < lay.depth; d++) alt[d] = (hb[d] ?? MAPA_BLOCO_H) * lay.stack[d] + MAPA_GAP * (lay.stack[d] - 1)
  const topo: number[] = []
  let acc = 0
  for (let d = 0; d < lay.depth; d++) {
    topo.push(acc)
    acc += (alt[d] ?? MAPA_BLOCO_H) + MAPA_GAP
  }
  const largura = lay.cols * MAPA_COL
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flexShrink: 0 }}>
    <Box data-testid="org-mapa" aria-label={t.units.orgChart.title} sx={{ position: 'relative', flexShrink: 0, width: largura, height: acc }}>
      {lay.blocks.map((b) => {
        const n = porId.get(b.id)
        if (!n) return null
        const faixa = faixaDoCargo(b.id, cores)
        return (
          <ButtonBase
            key={b.id}
            data-testid={`org-${b.id}`}
            data-nivel={b.depth}
            data-pilha={b.stacked ? String(b.k) : ''}
            data-faixa={faixa}
            onClick={() => setAberta(n)}
            aria-label={t.units.orgChart.openArea(n.unit.name, n.total)}
            sx={{
              position: 'absolute',
              left: b.col * MAPA_COL,
              top: topo[b.depth] + b.k * ((hb[b.depth] ?? MAPA_BLOCO_H) + MAPA_GAP),
              width: b.cols * MAPA_COL - MAPA_GAP,
              height: b.stacked || b.leaf ? (hb[b.depth] ?? MAPA_BLOCO_H) : alt[b.depth],
              bgcolor: '#FFFFFF',
              border: '1px solid #CBD5E1',
              borderTop: `4px solid ${faixa}`,
              borderRadius: 1,
              color: PALETA_AREAS.texto,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              px: 0.5,
              py: 0.75,
              '&:hover, &:focus-visible': { boxShadow: `0 0 0 2px ${faixa}` },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                lineHeight: 1.15,
                textAlign: 'center',
                // quebra SO entre palavras (a coluna ja cabe a maior palavra; nada de hifen nem corte no meio)
                overflowWrap: 'normal',
                wordBreak: 'normal',
                hyphens: 'manual',
              }}
            >
              {n.unit.name}
            </Typography>
            <Typography
              variant="caption"
              data-testid="mapa-pessoas"
              sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
            >
              {t.units.orgChart.people(n.total)}
            </Typography>
          </ButtonBase>
        )
      })}
    </Box>
      {advisory.length > 0 && (
        <Box data-testid="org-assessorias" sx={{ flexShrink: 0, width: Math.max(MAPA_COL, 180) }}>
          <Typography variant="caption" component="p" sx={{ fontWeight: 700, color: brand.navy, mb: 0.5, letterSpacing: 0.5 }}>
            {t.units.orgChart.advisoryColumn}
          </Typography>
          <Stack spacing={0.5}>
            {advisory.map((n) => {
              const faixa = faixaDoCargo(n.unit.id, cores)
              const filhas: OrgChartNode[] = []
              const desce = (x: OrgChartNode) => x.children.forEach((f) => (filhas.push(f), desce(f)))
              desce(n)
              return (
                <ButtonBase
                  key={n.unit.id}
                  data-testid={`org-${n.unit.id}`}
                  data-assessoria="sim"
                  data-faixa={faixa}
                  onClick={() => setAberta(n)}
                  aria-label={t.units.orgChart.openArea(n.unit.name, n.total)}
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    bgcolor: '#FFFFFF',
                    border: '1px dashed #94A3B8',
                    borderTop: `4px solid ${faixa}`,
                    borderRadius: 1,
                    color: PALETA_AREAS.texto,
                    px: 0.75,
                    py: 0.75,
                    width: '100%',
                    '&:hover, &:focus-visible': { boxShadow: `0 0 0 2px ${faixa}` },
                  }}
                >
                  <Typography variant="caption" component="p" sx={{ fontWeight: 700, lineHeight: 1.15, overflowWrap: 'normal', hyphens: 'manual' }}>
                    {n.unit.name}
                  </Typography>
                  {filhas.map((f) => (
                    <Typography key={f.unit.id} variant="caption" component="p" sx={{ lineHeight: 1.15, color: 'text.secondary' }}>
                      {f.unit.name}
                    </Typography>
                  ))}
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                    {t.units.orgChart.people(n.total)}
                  </Typography>
                </ButtonBase>
              )
            })}
          </Stack>
        </Box>
      )}
      {aberta && (
        <Dialog open onClose={() => setAberta(null)} maxWidth="md" aria-labelledby="area-titulo" data-testid="area-dialogo">
          <DialogTitle id="area-titulo">{aberta.unit.name}</DialogTitle>
          <DialogContent dividers data-testid="area-caixa">
            {podeMarcar && <MarcaAssessoria unitId={aberta.unit.id} valor={aberta.unit.isAdvisory === true} />}
            <Caixa
              titulo={aberta.unit.name}
              total={aberta.total}
              jobs={aberta.unit.jobs}
              staffed={aberta.unit.staffed}
              tom={{ bg: '#FFFFFF', fg: brand.navy, fg2: 'text.secondary', faixa: faixaDoCargo(aberta.unit.id, cores), borda: '#CBD5E1', escuro: false }}
              {...c}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAberta(null)}>{t.units.orgChart.close}</Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  )
}

/** X3: marca de ASSESSORIA na area (so quem altera unidades). Gravou -> o mapa recarrega e a area muda de lugar. */
function MarcaAssessoria({ unitId, valor }: { unitId: string; valor: boolean }) {
  const qc = useQueryClient()
  const [v, setV] = useState(valor)
  const m = useMutation({
    mutationFn: (x: boolean) => setUnitAdvisory(unitId, x),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ORG_CHART_QUERY_KEY }),
    onError: () => setV(valor),
  })
  return (
    <Box sx={{ mb: 1.5 }}>
      <FormControlLabel
        control={
          <Switch
            checked={v}
            disabled={m.isPending}
            onChange={(e) => {
              setV(e.target.checked)
              m.mutate(e.target.checked)
            }}
          />
        }
        label={t.units.orgChart.advisoryToggle}
      />
      <Typography variant="caption" color="text.secondary" component="p">
        {t.units.orgChart.advisoryHint}
      </Typography>
      {m.isError && <Alert severity="error">{t.units.orgChart.advisoryError}</Alert>}
    </Box>
  )
}

function Nomes({ job, fechar }: { job: OrgChartJob; fechar: () => void }) {
  const q = useQuery({ queryKey: orgChartPeopleKey(job.jobId), queryFn: () => fetchOrgChartPeople(job.jobId), retry: false })
  return (
    <Dialog open onClose={fechar} fullWidth maxWidth="xs" aria-labelledby="org-nomes-titulo">
      <DialogTitle id="org-nomes-titulo">{job.jobName}</DialogTitle>
      <DialogContent dividers>
        {q.isPending && <LoadingState />}
        {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
        {q.isSuccess && q.data.length === 0 && <Typography variant="body2">{t.units.orgChart.namesEmpty}</Typography>}
        {q.isSuccess && q.data.length > 0 && (
          <List dense disablePadding aria-label={job.jobName}>
            {q.data.map((p) => (
              <ListItem key={p.id} disableGutters>
                <Typography variant="body2">{p.name}</Typography>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={fechar}>{t.units.orgChart.close}</Button>
      </DialogActions>
    </Dialog>
  )
}

/** O1: janela com rolagem nas duas direcoes, arrastar para mover e zoom. */
function Janela({ children, ajustarAoAbrir = false }: { children: React.ReactNode; ajustarAoAbrir?: boolean }) {
  const janela = useRef<HTMLDivElement>(null)
  const conteudo = useRef<HTMLDivElement>(null)
  const [zoom, setZoom] = useState(1)
  const [tam, setTam] = useState({ w: 0, h: 0 })
  const arrasto = useRef<{ x: number; y: number; l: number; t: number } | null>(null)

  useLayoutEffect(() => {
    const el = conteudo.current
    if (!el) return
    const medir = () => setTam({ w: el.offsetWidth, h: el.offsetHeight })
    medir()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(medir)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const muda = (z: number) => setZoom(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100)))
  const ajustar = useCallback(() => {
    const j = janela.current
    if (!j || tam.w === 0 || tam.h === 0) return
    muda(Math.min(1, (j.clientWidth - 16) / tam.w, (j.clientHeight - 16) / tam.h))
  }, [tam])
  // s62 / C3a: o organograma de CARGOS e largo -- abre ja ajustado a tela (uma vez, quando a medida chega)
  const jaAjustou = useRef(false)
  useEffect(() => {
    if (!ajustarAoAbrir || jaAjustou.current || tam.w === 0) return
    jaAjustou.current = true
    ajustar()
  }, [ajustarAoAbrir, tam, ajustar])

  function inicio(e: ReactMouseEvent<HTMLDivElement>) {
    if (e.button !== 0 || (e.target as HTMLElement).closest('button, a, [role="button"]')) return
    const j = janela.current
    if (!j) return
    arrasto.current = { x: e.clientX, y: e.clientY, l: j.scrollLeft, t: j.scrollTop }
  }
  function move(e: ReactMouseEvent<HTMLDivElement>) {
    const a = arrasto.current
    const j = janela.current
    if (!a || !j) return
    j.scrollLeft = a.l - (e.clientX - a.x)
    j.scrollTop = a.t - (e.clientY - a.y)
  }
  const fim = () => {
    arrasto.current = null
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={0.5} alignItems="center" role="group" aria-label={t.units.orgChart.zoomGroup}>
        <Tooltip title={t.units.orgChart.zoomOut}>
          <span>
            <IconButton size="small" aria-label={t.units.orgChart.zoomOut} onClick={() => muda(zoom - ZOOM_PASSO)} disabled={zoom <= ZOOM_MIN}>
              <Remove fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="body2" data-testid="org-zoom" sx={{ minWidth: 44, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
          {Math.round(zoom * 100)}%
        </Typography>
        <Tooltip title={t.units.orgChart.zoomIn}>
          <span>
            <IconButton size="small" aria-label={t.units.orgChart.zoomIn} onClick={() => muda(zoom + ZOOM_PASSO)} disabled={zoom >= ZOOM_MAX}>
              <Add fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Button size="small" startIcon={<FitScreen fontSize="small" />} onClick={ajustar}>
          {t.units.orgChart.zoomFit}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
          {t.units.orgChart.dragHint}
        </Typography>
      </Stack>
      <Paper
        variant="outlined"
        ref={janela}
        data-testid="org-janela"
        onMouseDown={inicio}
        onMouseMove={move}
        onMouseUp={fim}
        onMouseLeave={fim}
        sx={{
          height: 'calc(100vh - 300px)',
          minHeight: 360,
          overflow: 'scroll',
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
          userSelect: 'none',
          bgcolor: '#F8FAFC',
        }}
      >
        <Box sx={{ width: tam.w ? tam.w * zoom : 'auto', height: tam.h ? tam.h * zoom : 'auto', m: 'auto' }}>
          <Box ref={conteudo} sx={{ display: 'inline-block', p: 3, transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
            {children}
          </Box>
        </Box>
      </Paper>
    </Stack>
  )
}

// ------------------------------------------------------------------ s62 / C3a: organograma de CARGOS em bandas
const COL = 200
const CEL_W = 176
const CEL_H = 70
const PASSO = 76
const FOLGA = 28
const ROTULO = 168

/**
 * s62 / C3a v2 (Marcelo 19/10 22:10: "nao pintar dentro da celula, somente a cor da faixa superior diferente"): cartao
 * BRANCO com texto navy; so a faixa de 4 px do topo muda -- cor da AREA-MAE pela paleta da Fabrica (marca v1.6.0).
 * Cargo da propria DIRECAO = faixa navy; cargo sem area = faixa cinza. O nome da area vai escrito (regra do kit).
 */
function faixaDoCargo(areaId: string | null, cores: Map<string, number | null>): string {
  if (areaId === null || !cores.has(areaId)) return brand.grey
  const i = cores.get(areaId)
  return i === null || i === undefined ? brand.navy : PALETA_AREAS.cores[i].faixa
}

function GraficoCargos({ d, podeNomes, abrir, comFolha }: { d: OrgChart; podeNomes: boolean; abrir: (j: OrgChartJob) => void; comFolha: boolean }) {
  // s62 / C3b-2 (ADR-0032): linhas = faixas DA EMPRESA; o cargo vai na faixa da sua FUNCAO (sem funcao: a sugerida)
  const plano = useMemo(() => planBands(d, (k) => t.units.orgChart.bands[k], suggestFunction), [d])
  const lay = useMemo(() => layoutJobBands(d, plano.rows.length, plano.bandOf), [d, plano])
  const cores = useMemo(() => areaMotherIndex(d.units), [d])
  const topo: number[] = []
  let acc = 0
  for (let b = 0; b < plano.rows.length; b++) {
    topo.push(acc)
    acc += lay.rows[b] * PASSO + FOLGA * 2
  }
  const largura = ROTULO + Math.max(1, lay.cols) * COL
  const pos = new Map(lay.placed.map((p) => [p.job.jobId, p]))
  const cx = (p: PlacedJob) => ROTULO + p.x * COL
  const cy = (p: PlacedJob) => topo[p.band] + FOLGA + p.k * PASSO
  return (
    <Box data-testid="org-cargos" sx={{ position: 'relative', width: largura, height: acc }} aria-label={t.units.orgChart.title}>
      {plano.rows.map((row, i) => (
        <Box
          key={row.key}
          data-testid={`banda-${row.key}`}
          sx={{ position: 'absolute', left: 0, top: topo[i], width: largura, height: lay.rows[i] * PASSO + FOLGA * 2, bgcolor: i % 2 === 0 ? '#F1F5F9' : '#F8FAFC', borderTop: '1px solid #E2E8F0' }}
        >
          <Typography variant="caption" sx={{ position: 'absolute', left: 8, top: 8, width: ROTULO - 16, fontWeight: 700, color: brand.navy, lineHeight: 1.25, textAlign: 'left' }}>
            {row.label}
          </Typography>
        </Box>
      ))}
      <Box component="svg" aria-hidden sx={{ position: 'absolute', left: 0, top: 0, width: largura, height: acc, pointerEvents: 'none' }}>
        {lay.placed.map((p) => {
          const m = p.parentId ? pos.get(p.parentId) : undefined
          if (!m || (p.stacked && p.k > 0)) return null
          const x1 = cx(m)
          const y1 = cy(m) + CEL_H
          const x2 = cx(p)
          const y2 = cy(p)
          const meio = y1 + Math.min(FOLGA, (y2 - y1) / 2)
          return (
            <path
              key={p.job.jobId}
              data-testid={`liga-${m.job.jobId}-${p.job.jobId}`}
              d={`M ${x1} ${y1} V ${meio} H ${x2} V ${y2}`}
              fill="none"
              stroke={brand.navy}
              strokeWidth={LINHA}
            />
          )
        })}
      </Box>
      {lay.placed.map((p) => {
        const faixa = faixaDoCargo(p.areaId, cores)
        const vago = comFolha && p.job.people === 0
        const sugerida = plano.suggested(p.job)
        const corpo = (
          <Box sx={{ width: '100%', textAlign: 'left' }}>
            <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2, color: vago ? 'text.secondary' : PALETA_AREAS.texto, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {p.job.jobName}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.areaName ?? t.units.orgChart.withoutArea}
              </Typography>
              <Typography variant="caption" sx={{ color: vago ? 'text.secondary' : PALETA_AREAS.texto, border: vago ? undefined : `1.5px solid ${brand.orange}`, borderRadius: 10, px: 0.75, fontVariantNumeric: 'tabular-nums' }}>
                {vago ? t.units.orgChart.vacant : p.job.people}
              </Typography>
            </Stack>
            {sugerida && (
              <Typography variant="caption" component="p" data-testid="funcao-sugerida" sx={{ color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.2 }}>
                {t.units.orgChart.suggestedBand}
              </Typography>
            )}
            {p.job.pendingAreaName && (
              <Typography variant="caption" component="p" sx={{ color: brand.orangeDark, fontWeight: 600, lineHeight: 1.2 }}>
                {t.units.orgChart.pendingArea(p.job.pendingAreaName)}
              </Typography>
            )}
          </Box>
        )
        const sx = {
          position: 'absolute' as const,
          left: cx(p) - CEL_W / 2,
          top: cy(p),
          width: CEL_W,
          minHeight: CEL_H,
          p: 1,
          borderRadius: 1.5,
          // s62 (Marcelo 20/10 16:12): SUGERIDA = borda toda tracejada, inclusive a faixa colorida do topo, e mais escura
          border: sugerida ? '1.5px dashed #64748B' : '1px solid #CBD5E1',
          borderTop: `4px ${sugerida ? 'dashed' : 'solid'} ${faixa}`,
          background: vago ? '#F1F5F9' : '#FFFFFF',
          color: PALETA_AREAS.texto,
          boxShadow: '0 1px 2px rgba(11,37,69,0.12)',
          alignItems: 'flex-start',
        }
        const attrs = { 'data-testid': `cargo-${p.job.jobId}`, 'data-banda': plano.rows[p.band]?.key ?? '', 'data-sugerida': sugerida ? 'sim' : 'nao', 'data-faixa': faixa, 'data-pilha': p.stacked ? String(p.k) : '', 'data-vago': vago ? 'sim' : 'nao' }
        return podeNomes ? (
          <ButtonBase key={p.job.jobId} {...attrs} onClick={() => abrir(p.job)} aria-label={t.units.orgChart.seeNames(p.job.jobName, p.job.people)} sx={sx}>
            {corpo}
          </ButtonBase>
        ) : (
          <Box key={p.job.jobId} {...attrs} sx={sx}>
            {corpo}
          </Box>
        )
      })}
    </Box>
  )
}

export function OrgChartView({ modo = 'cargos' }: { modo?: 'cargos' | 'areas' }) {
  const q = useQuery({ queryKey: ORG_CHART_QUERY_KEY, queryFn: fetchOrgChart, retry: false })
  const me = useQuery({ queryKey: ME_QUERY_KEY, queryFn: fetchMe })
  const podeMarcar = (me.data?.permissions ?? []).includes('organization_core.unit.write')
  const [aberto, setAberto] = useState<OrgChartJob | null>(null)
  const arvore = useMemo(() => buildOrgTree(q.data?.units ?? []), [q.data])
  const fora = useMemo(() => staffedElsewhere(q.data?.units ?? []), [q.data])

  if (q.isPending) return <LoadingState />
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />
  const d = q.data
  const comFolha = d.payrollBaseDate !== null
  // total do topo: pessoas com cargo ativo (contrato da s60b); quem esta sem cargo vai no aviso
  const total = d.units.reduce((s, u) => s + u.jobs.reduce((x, j) => x + j.people, 0), 0) + d.jobsWithoutArea.reduce((s, j) => s + j.people, 0)
  const comum: Comum = { podeNomes: d.canSeeNames, abrir: setAberto, fora, comFolha }

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={2} alignItems="baseline" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" sx={{ fontWeight: 600 }} data-testid="org-folha">
          {d.payrollBaseDate ? t.units.orgChart.payrollDate(dataBr(d.payrollBaseDate)) : t.units.orgChart.payrollUnknown}
        </Typography>
        <Typography variant="body2" color="text.secondary" data-testid="org-total">
          {t.units.orgChart.totalPeople(total)}
        </Typography>
      </Stack>
      {!d.canSeeNames && <Alert severity="info">{t.units.orgChart.namesOnlyHr}</Alert>}
      {d.peopleWithoutJob > 0 && <Alert severity="warning">{t.units.orgChart.peopleWithoutJob(d.peopleWithoutJob)}</Alert>}
      {modo === 'areas' && (
        <Janela>
          <Stack direction="row" spacing={4} alignItems="flex-start">
            <MapaAreas arvore={arvore} podeMarcar={podeMarcar} {...comum} />
            {d.jobsWithoutArea.length > 0 && (
              <Box data-testid="org-sem-area" sx={{ maxWidth: 300 }}>
                <Caixa
                  titulo={t.units.orgChart.withoutArea}
                  total={d.jobsWithoutArea.reduce((s, j) => s + j.people, 0)}
                  jobs={d.jobsWithoutArea}
                  tom={TOM_SEM_AREA}
                  {...comum}
                  fora={SEM_FORA}
                />
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5, textAlign: 'left' }}>
                  {t.units.orgChart.withoutAreaHint}
                </Typography>
              </Box>
            )}
          </Stack>
        </Janela>
      )}
      {modo === 'cargos' && (
        <>
          <Typography variant="caption" color="text.secondary" component="p">
            {t.units.orgChart.bandsHint}
          </Typography>
          <Janela ajustarAoAbrir>
            <GraficoCargos d={d} podeNomes={d.canSeeNames} abrir={setAberto} comFolha={comFolha} />
          </Janela>
        </>
      )}
      {aberto && <Nomes job={aberto} fechar={() => setAberto(null)} />}
    </Stack>
  )
}
